import {
    IRawRestrictionEntityType,
    IRawRestrictionEntityTypeValue,
    IRawRestrictionType
} from "./raw-restriction-types.interface";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {IRestrictionValue} from "./values/restriction-value.interface";
import {RESTRICTION_ENTITY_TYPE} from "./values/restrictions-constants";
import {GenericRestrictionValue} from "./values/generic-restriction-value";
import {NoRestrictionValue} from "./values/no-restriction-value";
import {GameInfo} from "../games/game-info";


export class RestrictionsCollection {
    constructor(private readonly rawRestrictions: IRawRestrictionType[],
                private readonly services: IServiceFactoryExtended) {
        this._loadRestrictions();
    }

    private static NoRestriction: IRestrictionValue = new NoRestrictionValue();

    private _allGameTypesRestrictions: IRestrictionValue[] = [];
    private _gameTypesRestrictions: Record<string, IRestrictionValue[]> = {};
    private _gameGroupsRestrictions: Record<string, IRestrictionValue[]> = {};
    private _gameVendorsRestrictions: Record<string, IRestrictionValue[]> = {};
    private _productPackageRestrictions: IRestrictionValue[] = [];
    private _subBrandRestrictions: IRestrictionValue[] = [];
    private _offeringRestrictions: IRestrictionValue[] = [];

    getGameRestriction(gameInfo: GameInfo): IRestrictionValue {
        return this._getFirstNonExpiredRestriction([
            ...this._allGameTypesRestrictions,
            ...(this._gameTypesRestrictions[gameInfo.gameType] ?? []),
            ...(this._gameGroupsRestrictions[gameInfo.gameGroupId] ?? []),
            ...(this._gameVendorsRestrictions[gameInfo.vendorId] ?? []),
            ...this._productPackageRestrictions,
            ...this._subBrandRestrictions,
            ...this._offeringRestrictions
        ]);
    }

    private get currentProductPackage(): number {
        return this.services.sdk.getProductPackageId();
    }

    private get currentSubBrandId(): number {
        return this.services.sdk.getSubBrandID();
    }


    public isProductPackageRestrictedForOpen(): boolean {
        return this._productPackageRestrictions.some(r => r.isActiveRestrictionForOpenApplication());
    }

    public isSubBrandRestrictedForOpen(): boolean {
        return this._subBrandRestrictions.some(r => r.isActiveRestrictionForOpenApplication());
    }

    private _loadRestrictions(): void {
        for(let rawRestrictionType of this.rawRestrictions) {
            for(let rawRestrictionEntity of rawRestrictionType.Entities) {
                this._appendEntityRestrictions(rawRestrictionType.ActionId, rawRestrictionEntity);
            }
        }
    }

    private _createRestrictionValue(actionId: number, rawRestrictionValue: IRawRestrictionEntityTypeValue): IRestrictionValue {
        return new GenericRestrictionValue(actionId, rawRestrictionValue, this.services);
    }

    private _appendEntityRestrictions(actionId: number, rawRestrictionEntity: IRawRestrictionEntityType): void {
        switch (rawRestrictionEntity.EntityId) {
            case RESTRICTION_ENTITY_TYPE.CHILDISH_GAME:
                //restriction entity 10 (childish games)  is ignored by Orbit, behind the scenes Platform are converting it to restriction on play/open application for all the relevant game type
                break;
            case RESTRICTION_ENTITY_TYPE.GAME_TYPE:
               this._appendGameTypeRestrictions(actionId, rawRestrictionEntity);
               break;
            case RESTRICTION_ENTITY_TYPE.GAME_GROUP:
                this._appendRestrictionsToDictionary(actionId, rawRestrictionEntity, this._gameGroupsRestrictions);
                break;
            case RESTRICTION_ENTITY_TYPE.GAME_VENDOR:
                this._appendRestrictionsToDictionary(actionId, rawRestrictionEntity, this._gameVendorsRestrictions);
                break;
            case RESTRICTION_ENTITY_TYPE.PACKAGE:
                this._appendRestrictionsToArray(actionId, rawRestrictionEntity, this._productPackageRestrictions, this.currentProductPackage.toString());
                break;
            case RESTRICTION_ENTITY_TYPE.SUB_BRAND:
                this._appendRestrictionsToArray(actionId, rawRestrictionEntity, this._subBrandRestrictions, this.currentSubBrandId.toString());
                break;
            case RESTRICTION_ENTITY_TYPE.OFFERING:
                this._appendRestrictionsToArray(actionId, rawRestrictionEntity, this._offeringRestrictions, '1'); // 1=’Casino Games’, 2='Poker Cash' and 3='Poker Tournament'
                break;
            default:
                this.services.logger.error(`Unsupported restriction entity ${rawRestrictionEntity.EntityId}`);
        }
    }

    private _appendGameTypeRestrictions(actionId: number, rawRestrictionEntity: IRawRestrictionEntityType): void {
        for(let rawValue of rawRestrictionEntity.Values) {
            const restrictionValue = this._createRestrictionValue(actionId, rawValue);
            if(restrictionValue.isExpired()) {
                continue;
            }
            if(rawValue.Value?.toString()?.toLowerCase() === 'all') {
                this._allGameTypesRestrictions.push(restrictionValue);
            } else {
                if(!this._gameTypesRestrictions[rawValue.Value]) {
                    this._gameTypesRestrictions[rawValue.Value] = [];
                }
                this._gameTypesRestrictions[rawValue.Value].push(restrictionValue);
            }
        }
    }

    private _appendRestrictionsToDictionary(actionId: number, rawRestrictionEntity: IRawRestrictionEntityType, dictionary: Record<string, IRestrictionValue[]>): void {
        for(let rawValue of rawRestrictionEntity.Values) {
            const restrictionValue = this._createRestrictionValue(actionId, rawValue);
            if(restrictionValue.isExpired()) {
                continue;
            }
            if(!dictionary[rawValue.Value]) {
                dictionary[rawValue.Value] = [];
            }
            dictionary[rawValue.Value].push(restrictionValue);
        }
    }

    private _appendRestrictionsToArray(actionId: number, rawRestrictionEntity: IRawRestrictionEntityType, array: IRestrictionValue[], forValue: string): void {
        for(let rawValue of rawRestrictionEntity.Values) {
            if(rawValue.Value?.toString() !== forValue) {
                continue;
            }
            const restrictionValue = this._createRestrictionValue(actionId, rawValue);
            if(restrictionValue.isExpired()) {
                continue;
            }

            array.push(restrictionValue);
        }
    }

    private _getFirstNonExpiredRestriction(restrictions: IRestrictionValue[]): IRestrictionValue {
        for(let restriction of restrictions) {
            if(!restriction.isExpired()) {
                return restriction;
            }
        }

        return RestrictionsCollection.NoRestriction;
    }

    //For debugging purpose
    printRestrictions(): void {
        this.services.logger.error("Restrictions", this.rawRestrictions);
    }


}